import snakeCase from 'lodash/snakeCase'

import { EventName } from './events'

export type TrackingPropertyValue = string | number | boolean | undefined
export type TrackingProperties = Record<string, TrackingPropertyValue>

const SKIP_DATASET_KEYS = new Set(['track', 'trackImpression'])

const AFFILIATE_ALIASES: Record<string, string> = {
  affiliateCta: 'cta',
  affiliateText: 'text',
  affiliateTestName: 'testName',
  affiliateSellerType: 'partnerType',
}

const EVENT_ALIASES: Record<string, string> = {
  eventListiclePosition: 'listiclePosition',
}

const parseMaybeNumber = (value: TrackingPropertyValue): TrackingPropertyValue => {
  if (typeof value !== 'string' || !/^\d+$/.test(value)) {
    return value
  }
  return Number(value)
}

const setIfMissing = (properties: TrackingProperties, key: string, value: TrackingPropertyValue) => {
  if (value !== undefined && properties[key] === undefined) {
    properties[key] = value
  }
}

const legacyEventKey = (key: string): string | null => {
  if (!key.startsWith('event') || key === 'eventName') {
    return null
  }
  return snakeCase(key.replace(/^event/, ''))
}

export const normalizeTrackingProperties = (
  raw: TrackingProperties,
  options: { eventName?: string } = {}
): TrackingProperties => {
  const properties: TrackingProperties = {}

  for (const [key, value] of Object.entries(raw)) {
    if (SKIP_DATASET_KEYS.has(key)) {
      continue
    }

    properties[key] = value

    const eventKey = legacyEventKey(key)
    if (eventKey) {
      setIfMissing(properties, eventKey, value)
    }

    const eventAlias = EVENT_ALIASES[key]
    if (eventAlias) {
      setIfMissing(properties, eventAlias, value)
    }

    const affiliateAlias = AFFILIATE_ALIASES[key]
    if (affiliateAlias) {
      setIfMissing(properties, affiliateAlias, value)
    }

    if (key === 'affiliateSegment') {
      setIfMissing(properties, 'affiliate_segment', parseMaybeNumber(value))
    }
  }

  if (options.eventName === EventName.AffiliateAction) {
    setIfMissing(properties, 'release', raw.eventProduct)
    setIfMissing(properties, 'testId', raw.eventProduct)
    setIfMissing(properties, 'seller', raw.eventSeller)
    setIfMissing(properties, 'link', raw.eventUrl)
    setIfMissing(properties, 'testName', raw.affiliateTestName ?? raw.eventPosition)
    setIfMissing(properties, 'elementType', 'button')
  }

  setIfMissing(properties, 'link', raw.eventUrl)

  return properties
}
